import React from 'react';
import Header from 'components/ui/Header';
import Footer from 'components/Footer';
import Icon from 'components/AppIcon';
import Button from 'components/ui/Button';
import SEO from 'components/SEO';
import { useNavigate } from 'react-router-dom';

const MobileAppDevelopment = () => {
  const navigate = useNavigate();

  const seoData = {
    title: 'Mobile App Development Services - iOS & Android Apps | OctalChip',
    description: 'Native and cross-platform mobile applications for iOS and Android. We build scalable apps using React Native, Flutter, and native development for optimal performance.',
    keywords: 'mobile app development, iOS development, Android development, React Native, Flutter, cross-platform apps',
    canonical: 'https://www.octalchip.com/services/mobile-app-development',
    structuredData: {
      "@context": "https://schema.org",
      "@type": "Service",
      "name": "Mobile App Development Services",
      "description": "Native and cross-platform mobile applications for iOS and Android",
      "provider": {
        "@type": "Organization",
        "name": "OctalChip"
      }
    }
  };

  const features = [
    {
      title: 'Cross-platform Development',
      description: 'Build once, deploy everywhere with React Native and Flutter for iOS and Android',
      icon: 'Smartphone'
    },
    {
      title: 'Native Performance',
      description: 'Optimized apps that deliver native-like performance and smooth user experience',
      icon: 'Zap'
    },
    {
      title: 'App Store Optimization',
      description: 'Expert ASO strategies to maximize visibility and downloads in app stores',
      icon: 'TrendingUp'
    },
    {
      title: 'Push Notifications',
      description: 'Engage users with real-time push notifications and in-app messaging',
      icon: 'Bell'
    },
    {
      title: 'Offline Functionality',
      description: 'Apps that work seamlessly offline with data synchronization when online',
      icon: 'Wifi'
    },
    {
      title: 'Security & Privacy',
      description: 'Enterprise-grade security with encryption, biometric authentication, and GDPR compliance',
      icon: 'Shield'
    }
  ];

  const technologies = [
    { name: 'React Native', category: 'Framework', description: 'Cross-platform mobile development with JavaScript' },
    { name: 'Flutter', category: 'Framework', description: 'Google\'s UI toolkit for beautiful native apps' },
    { name: 'iOS (Swift)', category: 'Native', description: 'Native iOS development with Swift and SwiftUI' },
    { name: 'Android (Kotlin)', category: 'Native', description: 'Native Android development with Kotlin' },
    { name: 'Firebase', category: 'Backend', description: 'Backend services for authentication, database, and analytics' },
    { name: 'Xamarin', category: 'Framework', description: 'Microsoft framework for cross-platform development' },
    { name: 'Ionic', category: 'Framework', description: 'Hybrid app framework with web technologies' },
    { name: 'App Store Connect', category: 'Platform', description: 'iOS app distribution and management' },
    { name: 'Google Play Console', category: 'Platform', description: 'Android app distribution and management' }
  ];

  const useCases = [
    {
      title: 'E-commerce Mobile Apps',
      description: 'Feature-rich shopping apps with secure payments, wishlists, and order tracking',
      icon: 'ShoppingCart'
    },
    {
      title: 'Social Media Apps',
      description: 'Engaging social platforms with real-time messaging, feeds, and content sharing',
      icon: 'Users'
    },
    {
      title: 'Healthcare Apps',
      description: 'HIPAA-compliant apps for telemedicine, patient records, and appointment scheduling',
      icon: 'Heart'
    },
    {
      title: 'Fitness & Wellness',
      description: 'Track workouts, monitor health metrics, and connect with fitness communities',
      icon: 'Activity'
    },
    {
      title: 'Food Delivery Apps',
      description: 'Complete food ordering systems with real-time tracking and payment integration',
      icon: 'Utensils'
    },
    {
      title: 'Finance & Banking',
      description: 'Secure banking apps with transaction management, investments, and financial planning',
      icon: 'CreditCard'
    }
  ];

  const processSteps = [
    {
      step: '01',
      title: 'Strategy & Planning',
      description: 'Define app goals, target audience, platform strategy, and feature roadmap',
      icon: 'Target'
    },
    {
      step: '02',
      title: 'UI/UX Design',
      description: 'Create intuitive interfaces, user flows, and prototypes following platform guidelines',
      icon: 'Palette'
    },
    {
      step: '03',
      title: 'Development',
      description: 'Agile development with weekly sprints, code reviews, and continuous integration',
      icon: 'Code'
    },
    {
      step: '04',
      title: 'Testing & QA',
      description: 'Comprehensive testing on multiple devices, OS versions, and network conditions',
      icon: 'CheckCircle'
    },
    {
      step: '05',
      title: 'App Store Submission',
      description: 'Prepare and submit to App Store and Google Play with optimized listings',
      icon: 'Upload'
    },
    {
      step: '06',
      title: 'Launch & Support',
      description: 'Monitor performance, gather user feedback, and release regular updates',
      icon: 'Rocket'
    }
  ];

  const benefits = [
    'Reach customers on their preferred devices',
    'Increase engagement with push notifications',
    'Access device features like camera and GPS',
    'Work offline with data synchronization',
    'Better user experience than mobile websites',
    'Higher conversion rates and customer retention',
    'Brand presence on app stores',
    'Monetization opportunities through in-app purchases'
  ];

  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title={seoData.title}
        description={seoData.description}
        keywords={seoData.keywords}
        canonical={seoData.canonical}
        structuredData={seoData.structuredData}
      />
      <Header />
      
      {/* Enhanced Hero Section */}
      <section className="relative py-20 md:py-32 bg-gradient-to-br from-[#00d690]/10 via-white to-[#00d690]/5 overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 2px 2px, #00d690 1px, transparent 0)`,
            backgroundSize: '40px 40px'
          }}></div>
        </div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-5xl mx-auto text-center">
            <div className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-br from-[#00d690] to-[#00b876] rounded-2xl mb-8 shadow-lg transform hover:scale-110 transition-transform duration-300">
              <Icon name="Smartphone" size={48} className="text-white" />
            </div>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-gray-800 mb-6 leading-tight">
              Mobile App
              <span className="block text-[#00d690] mt-2">Development</span>
            </h1>
            <div className="w-32 h-1 bg-gradient-to-r from-[#00d690] to-[#00b876] mx-auto mb-8"></div>
            <p className="text-xl md:text-2xl text-gray-600 mb-12 max-w-3xl mx-auto leading-relaxed">
              Create powerful mobile applications for iOS and Android. We build native and cross-platform apps 
              that deliver exceptional user experiences and drive business growth.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button 
                variant="primary" 
                onClick={() => navigate('/services#share-your-requirements')}
                className="px-10 py-4 text-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition-all"
              >
                Start Your Project
              </Button>
              <Button 
                variant="outline" 
                onClick={() => navigate('/services#services')}
                className="px-10 py-4 text-lg border-2"
              >
                View All Services
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 bg-white border-b border-gray-100">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-6xl mx-auto">
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold text-[#00d690] mb-2">15+</div>
              <div className="text-gray-600 font-medium">Apps Delivered</div>
            </div>
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold text-[#00d690] mb-2">4-12</div>
              <div className="text-gray-600 font-medium">Weeks Timeline</div>
            </div>
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold text-[#00d690] mb-2">100K+</div>
              <div className="text-gray-600 font-medium">App Downloads</div>
            </div>
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold text-[#00d690] mb-2">4.8★</div>
              <div className="text-gray-600 font-medium">Average Rating</div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">Key Features</h2>
            <div className="w-24 h-1 bg-[#00d690] mx-auto mb-6"></div>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive mobile app features designed for modern users
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
            {features.map((feature, index) => (
              <div 
                key={index} 
                className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 group border border-gray-100"
              >
                <div className="w-16 h-16 bg-gradient-to-br from-[#00d690]/10 to-[#00b876]/10 rounded-xl flex items-center justify-center mb-6 group-hover:from-[#00d690] group-hover:to-[#00b876] transition-all duration-300">
                  <Icon name={feature.icon} size={28} className="text-[#00d690] group-hover:text-white transition-colors" />
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-3 group-hover:text-[#00d690] transition-colors">
                  {feature.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Technologies Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">Technologies We Use</h2>
            <div className="w-24 h-1 bg-[#00d690] mx-auto mb-6"></div>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Cutting-edge mobile development technologies and frameworks
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-7xl mx-auto">
            {technologies.map((tech, index) => (
              <div 
                key={index} 
                className="bg-gradient-to-br from-gray-50 to-white p-6 rounded-xl shadow-md hover:shadow-xl transition-all duration-300 border border-gray-100 group"
              >
                <div className="flex items-center justify-between mb-3">
                  <span className="text-lg font-bold text-gray-800 group-hover:text-[#00d690] transition-colors">
                    {tech.name}
                  </span>
                  <span className="text-xs font-medium text-[#00d690] bg-[#00d690]/10 px-2 py-1 rounded">
                    {tech.category}
                  </span>
                </div>
                <p className="text-sm text-gray-600 leading-relaxed">
                  {tech.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="py-20 bg-gradient-to-br from-[#00d690]/5 to-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">App Types We Build</h2>
            <div className="w-24 h-1 bg-[#00d690] mx-auto mb-6"></div>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              From simple utilities to complex enterprise applications
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
            {useCases.map((useCase, index) => (
              <div 
                key={index}
                className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:scale-105 group border-l-4 border-[#00d690]"
              >
                <div className="w-14 h-14 bg-[#00d690]/10 rounded-lg flex items-center justify-center mb-6 group-hover:bg-[#00d690] transition-all duration-300">
                  <Icon name={useCase.icon} size={28} className="text-[#00d690] group-hover:text-white transition-colors" />
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-3 group-hover:text-[#00d690] transition-colors">
                  {useCase.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {useCase.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">Our Development Process</h2>
            <div className="w-24 h-1 bg-[#00d690] mx-auto mb-6"></div>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              A proven methodology for successful mobile app development
            </p>
          </div>
          
          <div className="max-w-6xl mx-auto">
            <div className="relative">
              <div className="hidden md:block absolute left-8 top-0 bottom-0 w-1 bg-gradient-to-b from-[#00d690] to-[#00b876]"></div>
              
              <div className="space-y-12">
                {processSteps.map((step, index) => (
                  <div key={index} className="relative flex flex-col md:flex-row items-start gap-6">
                    <div className="flex-shrink-0 w-16 h-16 bg-gradient-to-br from-[#00d690] to-[#00b876] rounded-full flex items-center justify-center text-white font-bold text-lg shadow-lg z-10">
                      {step.step}
                    </div>
                    
                    <div className="flex-1 bg-gray-50 rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow border border-gray-100">
                      <div className="flex items-center mb-4">
                        <div className="w-12 h-12 bg-[#00d690]/10 rounded-lg flex items-center justify-center mr-4">
                          <Icon name={step.icon} size={24} className="text-[#00d690]" />
                        </div>
                        <h3 className="text-2xl font-bold text-gray-800">{step.title}</h3>
                      </div>
                      <p className="text-gray-600 leading-relaxed text-lg">
                        {step.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-gradient-to-br from-gray-800 to-gray-900 text-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold mb-4">Why Choose Mobile App Development?</h2>
              <div className="w-24 h-1 bg-[#00d690] mx-auto mb-6"></div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {benefits.map((benefit, index) => (
                <div key={index} className="flex items-start gap-4 bg-white/5 backdrop-blur-sm p-6 rounded-xl border border-white/10 hover:bg-white/10 transition-all">
                  <div className="flex-shrink-0 w-8 h-8 bg-[#00d690] rounded-full flex items-center justify-center">
                    <Icon name="Check" size={20} className="text-white" />
                  </div>
                  <p className="text-lg text-gray-200">{benefit}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 md:py-20 bg-gray-50">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center mb-8 md:mb-16">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-800 mb-4">
              Ready to Build Your Mobile App?
            </h2>
            <div className="w-16 md:w-24 h-1 bg-[#00d690] mx-auto mb-4 md:mb-6"></div>
            <p className="text-base md:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto mb-8">
              Let's discuss your app idea and create a solution that engages users and drives business growth. 
              Get a free consultation today.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => navigate('/services#share-your-requirements')}
                className="bg-[#00d690] hover:bg-[#00b876] text-white font-bold py-4 px-8 rounded-lg transition-all duration-300 transform hover:scale-105"
              >
                Start Your Project
              </button>
              <button
                onClick={() => navigate('/services#services')}
                className="border-2 border-[#00d690] text-[#00d690] hover:bg-[#00d690] hover:text-white font-bold py-4 px-8 rounded-lg transition-all duration-300"
              >
                View All Services
              </button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default MobileAppDevelopment;
